/**
 * Anemoia free gifts (custom).
 *
 * Keeps the gift products (snippets/anemoia-gifts.liquid) in step with the
 * cart: one of each while the cart holds at least one paid item, none once it
 * holds none. The gifts are $0 products, so this is purely about presence —
 * there is no discount to apply.
 *
 * Exempt pieces (config.exempt: the sellable Tote Bag) are paid but don't
 * earn the gifts: a cart holding only those gets none.
 *
 * Wires to the theme's `cart:update` event, which every add path fires (PDP,
 * home hero, collection float, the cart drawer's quick add, quantity changes
 * and removals), so no add path needs to know the gifts exist. Each change is
 * made in a single /cart/update.js call keyed by variant id, which both adds a
 * missing gift and pins an existing one at 1, and asks for the cart drawer's
 * sections back so the open drawer morphs the gifts in without a refetch.
 *
 * Our own add buttons (PDP, shop page) go one better and send the gifts in the
 * same /cart/add.js request as the piece, through window.AnemoiaGifts.add,
 * so the drawer opens with them already listed. Every other path still works,
 * one round trip later.
 *
 * The header cart count leaves the gifts out (snippets/cart-bubble.liquid does
 * the same on render). The theme's own events report the full item count, so
 * after each one the bubble is set again to the paid count. That lands after
 * <cart-icon>'s own handler, because it waits on the cart fetch.
 */

import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const SOURCE = 'anemoia-gifts';

function readConfig() {
  const node = document.getElementById('anemoia-gifts-config');
  if (!node) return null;
  try {
    const config = JSON.parse(node.textContent || '{}');
    return Array.isArray(config.gifts) && config.gifts.length ? config : null;
  } catch {
    return null;
  }
}

const config = readConfig();

if (config && !window.__anemoiaGifts) {
  window.__anemoiaGifts = true;

  const giftProducts = new Set(config.gifts.map((gift) => Number(gift.productId)));
  const exemptProducts = new Set((config.exempt || []).map((entry) => Number(entry.productId)));
  const exemptVariants = new Set((config.exempt || []).flatMap((entry) => (entry.variantIds || []).map(Number)));
  const routes = window.Theme?.routes || {};

  /** A line that earns the gifts: neither a gift nor an exempt piece. */
  const qualifies = (line) => {
    const id = Number(line.product_id);
    return !giftProducts.has(id) && !exemptProducts.has(id);
  };

  const paidCount = (cart) =>
    (cart.items || []).reduce(
      (sum, line) => (giftProducts.has(Number(line.product_id)) ? sum : sum + line.quantity),
      0
    );

  const syncBubble = (count) => {
    document.querySelectorAll('cart-icon').forEach((icon) => {
      if (typeof icon.renderCartBubble === 'function') icon.renderCartBubble(count, false, false);
    });
  };

  let running = false;
  let again = false;

  /** What the cart should change to, or null when it is already right. */
  const plan = (cart) => {
    const lines = cart.items || [];
    const paid = lines.some(qualifies);
    const updates = {};

    for (const gift of config.gifts) {
      const giftLines = lines.filter((line) => Number(line.product_id) === Number(gift.productId));
      const quantity = giftLines.reduce((sum, line) => sum + line.quantity, 0);

      if (!paid) {
        if (quantity > 0) updates[gift.variantId] = 0;
      } else if (quantity === 0) {
        if (gift.available) updates[gift.variantId] = 1;
      } else if (quantity !== 1 && giftLines.length === 1) {
        updates[giftLines[0].variant_id] = 1;
      }
    }

    return Object.keys(updates).length ? updates : null;
  };

  /** Gift product ids believed to be in the cart: set by Liquid, kept by every cart response seen. */
  const inCart = new Set((config.inCart || []).map(Number));
  const noteCart = (cart) => {
    inCart.clear();
    for (const line of cart.items || []) {
      if (giftProducts.has(Number(line.product_id))) inCart.add(Number(line.product_id));
    }
  };
  const missingGifts = () => config.gifts.filter((gift) => gift.available && !inCart.has(Number(gift.productId)));

  /**
   * The fast path, for our own add buttons: the gifts ride in the same
   * /cart/add.js request as the piece, so the drawer opens with them already in
   * it instead of morphing them in a round trip later. Resolves to the parsed
   * response. If that combined request is refused (say a gift sold out since
   * the page rendered) the piece is added on its own, so a gift can never cost
   * the shopper their add; the engine then catches up as usual.
   */
  window.AnemoiaGifts = {
    async add(url, items, sections) {
      const post = (list) =>
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: list, sections }),
        }).then((r) => r.json());

      const extra = missingGifts().map((gift) => ({ id: Number(gift.variantId), quantity: 1 }));
      const earns = (items || []).some((item) => !exemptVariants.has(Number(item.id)));
      if (!extra.length || !earns) return post(items);
      const data = await post([...items, ...extra]);
      return data?.status ? post(items) : data;
    },
  };

  const apply = async (updates) => {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    const response = await fetch(routes.cart_update_url ? `${routes.cart_update_url}.js` : '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: sectionIds.join(',') }),
    });
    const updated = await response.json();
    if (!response.ok || updated?.status) return;
    noteCart(updated);

    document.dispatchEvent(
      new CartUpdateEvent(updated, SOURCE, {
        source: SOURCE,
        itemCount: paidCount(updated),
        sections: updated.sections,
      })
    );
  };

  /**
   * Bring the cart in step. `resource` is whatever the triggering event carried:
   * a full cart (quantity changes, removals) is planned from directly, and an
   * add response (just the added lines) needs no cart at all, since an add can
   * only mean a paid piece is now in. Anything else falls back to /cart.js.
   */
  const reconcile = async (resource) => {
    if (running) {
      again = true;
      return;
    }
    running = true;

    try {
      const isFullCart = resource && Array.isArray(resource.items) && typeof resource.item_count === 'number';
      const addedLines = !isFullCart && resource ? (Array.isArray(resource.items) ? resource.items : resource.id ? [resource] : null) : null;

      if (addedLines && addedLines.some(qualifies)) {
        const updates = {};
        for (const line of addedLines) {
          if (!giftProducts.has(Number(line.product_id))) continue;
          inCart.add(Number(line.product_id));
          if (line.quantity !== 1) updates[line.variant_id] = 1;
        }
        for (const gift of missingGifts()) updates[gift.variantId] = 1;
        if (Object.keys(updates).length) await apply(updates);
        return;
      }

      const cart = isFullCart
        ? resource
        : await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
            r.json()
          );
      noteCart(cart);
      const updates = plan(cart);
      if (updates) await apply(updates);
      else syncBubble(paidCount(cart));
    } catch (error) {
      console.error('Anemoia gifts could not update the cart', error);
    } finally {
      running = false;
      if (again) {
        again = false;
        reconcile();
      }
    }
  };

  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    reconcile(event.detail?.resource);
  });

  if (config.checkOnLoad) reconcile();
}
